/**
 * 无限画布 NodeGraphCanvas 各节点模型选项（单一数据源）
 * 与 src/views/NodeGraphCanvas.vue 中 llm / image / video / audio 下拉保持一致
 */

/** 画布视频节点 seedanceModel 为空时，后端默认接入点（见 FastMovieController SeedanceModelId） */
export const NGC_SEEDANCE_BACKEND_DEFAULT = 'doubao-seedance-2-0-fast-260128'

/** Seedance 2.5（支持最长 30 秒） */
export const NGC_SEEDANCE_25_MODEL_ID = 'doubao-seedance-2-5-260628'

/** 是否为 Seedance 2.5（仅此系列开放 30 秒时长） */
export function isSeedance25Model(modelId) {
  const id = String(modelId || '')
    .trim()
    .toLowerCase()
  if (!id) return false
  return (
    id === NGC_SEEDANCE_25_MODEL_ID ||
    id.includes('seedance-2-5') ||
    id.includes('seedance-2.5')
  )
}

/** 视频节点时长上限：Seedance 2.5 / Grok → 30s，其余默认 15s */
export function seedanceMaxDurationSec(modelId) {
  if (isDuomiVideoModel(modelId)) return duomiVideoMaxDurationSec(modelId)
  return isSeedance25Model(modelId) ? 30 : 15
}

/** DM Sora / Veo / Grok（https://duomiapi.com/doc/98） */
export const DUOMI_VIDEO_MODEL_OPTIONS = [
  { id: 'sora-2', label: 'Sora 2（DM）', family: 'sora' },
  { id: 'veo3.1-fast', label: 'Veo 3.1 Fast（DM）', family: 'veo' },
  { id: 'veo3.1-pro', label: 'Veo 3.1 Pro（DM）', family: 'veo' },
  { id: 'grok-video', label: 'Grok Video（DM）', family: 'grok' },
  { id: 'grok-video-1.5', label: 'Grok Video 1.5（DM）', family: 'grok' }
]

export function duomiVideoFamily(modelId) {
  const id = String(modelId || '')
    .trim()
    .toLowerCase()
  if (!id) return ''
  const hit = DUOMI_VIDEO_MODEL_OPTIONS.find(m => m.id === id)
  if (hit) return hit.family
  if (id.startsWith('sora-')) return 'sora'
  if (id.startsWith('veo')) return 'veo'
  if (id.startsWith('grok-video')) return 'grok'
  return ''
}

export function isDuomiVideoModel(modelId) {
  return duomiVideoFamily(modelId) !== ''
}

/** 各家族允许的时长档位（秒） */
export function duomiVideoAllowedDurations(modelId) {
  const family = duomiVideoFamily(modelId)
  if (family === 'sora') return [4, 8, 12]
  if (family === 'veo') return [8]
  if (family === 'grok') return [6, 10, 15, 20, 25, 30]
  return [5, 10, 15]
}

export function duomiVideoMaxDurationSec(modelId) {
  const list = duomiVideoAllowedDurations(modelId)
  return list.length ? list[list.length - 1] : 15
}

/** 将任意秒数对齐到该模型允许档位 */
export function snapDuomiVideoDuration(modelId, sec) {
  const list = duomiVideoAllowedDurations(modelId)
  const n = Math.round(Number(sec) || list[0] || 5)
  if (!list.length) return Math.min(15, Math.max(1, n))
  let best = list[0]
  let bestDiff = Math.abs(n - best)
  for (let i = 1; i < list.length; i++) {
    const d = Math.abs(n - list[i])
    if (d < bestDiff) {
      best = list[i]
      bestDiff = d
    }
  }
  return best
}

/** DM视频宽高比：Sora/Veo 仅 16:9|9:16；Grok 支持更多 */
export function normalizeDuomiAspectRatio(modelId, ratio) {
  const family = duomiVideoFamily(modelId)
  const r = String(ratio || '')
    .trim()
    .replace('×', ':')
  if (family === 'sora' || family === 'veo') {
    if (r === '9:16') return '9:16'
    return '16:9'
  }
  if (family === 'grok') {
    const allow = ['16:9', '9:16', '1:1', '2:3', '3:2']
    if (allow.includes(r)) return r
    return '16:9'
  }
  return r || '16:9'
}

/** 参考图上限：grok-video-1.5 仅 1 张；其余按家族 */
export function duomiVideoMaxImages(modelId) {
  const id = String(modelId || '')
    .trim()
    .toLowerCase()
  if (id === 'grok-video-1.5' || id.includes('grok-video-1.5')) return 1
  const family = duomiVideoFamily(modelId)
  if (family === 'sora') return 1
  if (family === 'veo') return 3
  if (family === 'grok') return 7
  return 7
}

/** 提示词建议上限（过长易被DM侧软失败成「系统繁忙」） */
export function duomiVideoPromptMaxChars(modelId) {
  const family = duomiVideoFamily(modelId)
  if (family === 'grok') return 2800
  if (family === 'veo') return 3500
  if (family === 'sora') return 3500
  return 3000
}

/**
 * 清洗画布拼出来的超长提示词：去掉解析摘要/文生图表格/分镜线稿说明等，再按模型截断
 */
export function sanitizeDuomiVideoPrompt(prompt, modelId) {
  let t = prompt == null ? '' : String(prompt).trim()
  if (!t) return ' '
  const cutMarkers = [
    '【解析摘要',
    '### 角色提示词',
    '### 道具提示词',
    '### 场景提示词',
    '### 分镜时间轴',
    '| 名称 | 文生图提示词 |',
    '16:9 横屏 故事板表格风格',
    '实际故事板绘图必须仅为黑白',
    '标注颜色系统：',
    '[图像] 为故事板创建一个'
  ]
  let cutAt = -1
  for (const m of cutMarkers) {
    const i = t.indexOf(m)
    if (i >= 200 && (cutAt < 0 || i < cutAt)) cutAt = i
  }
  if (cutAt > 0) t = t.slice(0, cutAt).trim()
  // 未解析占位符对 Grok 无益，去掉以免干扰
  t = t
    .replace(/\{\{\s*(Mixed|Portrait|Image)\s*\d+\s*\}\}/gi, '')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
  const max = duomiVideoPromptMaxChars(modelId)
  if (t.length > max) {
    t = t.slice(0, max).replace(/\s+\S*$/, '').trim()
    if (!t.endsWith('…') && !t.endsWith('...')) t += '…'
  }
  return t || ' '
}

export const NGC_MODEL_GROUP = {
  LLM: '无限画布 · 文本 / 分镜',
  IMAGE: '无限画布 · 图片',
  VIDEO: '无限画布 · 视频',
  AUDIO: '无限画布 · 音频'
}

/** 文本节点 model、分镜/电商 deepseekModel（部分）；积分仅来自 GET /modelslist */
export const NGC_LLM_MODEL_OPTIONS = [
  { id: 'deepseek-v4-flash', label: 'DeepSeek V4 Flash' },
  { id: 'deepseek-v4-pro', label: 'DeepSeek V4 Pro' },
  {
    id: 'doubao-seed-character-251128',
    label: '豆包 Seed Character（火山方舟）'
  }
]

/** 图片节点 seedreamModel、电商出图 */
export const NGC_IMAGE_MODEL_OPTIONS = [
  { id: 'doubao-seedream-5-0-260128', label: 'Seedream 5.0' },
  { id: 'gpt-image-2', label: 'GPT-Image-2（API易）' },
  { id: 'gpt-image-2-duomi', label: 'GPT-Image-2-d' },
  { id: 'gpt-image-2.5-flare', label: 'GPT-Image 2.5 Flare' },
  { id: 'gpt-image-2.5-sunburst', label: 'GPT-Image 2.5 Sunburst' },
  { id: 'gpt-image-2-all', label: 'GPT-Image-2-All（API易·官逆）' },
  { id: 'gpt-image-2-vip', label: 'GPT-Image-2-VIP（API易·可锁 size）' },
  { id: 'nano-banana-2', label: 'Nano Banana 2（API易）' }
]

/** 视频节点 seedanceModel；id 为空表示 Seedance 2.0 VIP（走后端默认端点） */
export const NGC_VIDEO_MODEL_OPTIONS = [
  { id: '', label: 'Seedance 2.0 VIP', backendDefault: NGC_SEEDANCE_BACKEND_DEFAULT },
  {
    id: NGC_SEEDANCE_BACKEND_DEFAULT,
    label: 'Seedance 2.0 Fast（端点 ID）'
  },
  {
    id: NGC_SEEDANCE_25_MODEL_ID,
    label: 'Seedance 2.5'
  },
  ...DUOMI_VIDEO_MODEL_OPTIONS.map(({ id, label }) => ({ id, label })),
  /** 百炼 HappyHorse 1.1（文生/图生/参考；主条目按节点模式自动切 t2v/i2v/r2v） */
  { id: 'happyhorse-1.1-t2v', label: 'HappyHorse 1.1' },
  { id: 'happyhorse-1.1-i2v', label: 'HappyHorse 1.1 · 图生视频' },
  { id: 'happyhorse-1.1-r2v', label: 'HappyHorse 1.1 · 参考图生视频' },
  { id: 'happyhorse-1.0-t2v', label: 'HappyHorse 1.0' },
  { id: 'happyhorse-1.0-i2v', label: 'HappyHorse 1.0 · 图生视频' },
  { id: 'happyhorse-1.0-r2v', label: 'HappyHorse 1.0 · 参考图生视频' },
  { id: 'happyhorse-1.0-video-edit', label: 'HappyHorse 1.0 · 视频编辑' },
  { id: 'wan2.2-animate-move', label: '万相 · 动作模仿（Animate Move）' }
]

/** 音频节点 model */
export const NGC_AUDIO_MODEL_OPTIONS = [
  {
    id: 'volc-doubao-tts-2',
    label: '豆包·语音合成 2.0（OpenSpeech）'
  },
  {
    id: 'volc-doubao-icl-2',
    label: '豆包·声音复刻 2.0（上传参考音）'
  },
  {
    id: 'volc-doubao-tts-2-expressive',
    label: '豆包·合成 2.0 高表现（req model）'
  },
  // { id: 'minimax-speech-2.8-hd', label: '兼容：Minimax / yinpin 链路' }
]

const KIND_BY_GROUP = {
  [NGC_MODEL_GROUP.LLM]: 'text',
  [NGC_MODEL_GROUP.IMAGE]: 'image',
  [NGC_MODEL_GROUP.VIDEO]: 'video',
  [NGC_MODEL_GROUP.AUDIO]: 'audio'
}

function mapCanvasOption(m, group, nodeField) {
  const rawId = m && m.id != null ? String(m.id) : ''
  const value =
    rawId.trim() ||
    (m && m.backendDefault ? String(m.backendDefault).trim() : '')
  if (!value) return null
  const label = String((m && m.label) || value)
  const canvasId = rawId.trim()
  const suffix =
    canvasId === '' && m && m.backendDefault
      ? '（画布默认 · 端点 ID）'
      : ''
  return {
    value,
    label: label + suffix,
    name: String((m && m.label) || value).replace(/（画布默认 · 端点 ID）$/, ''),
    kind: KIND_BY_GROUP[group] || 'image',
    group,
    nodeField,
    canvasId
  }
}

/** 后台模型定价：按画布分组 + 扁平列表 */
export function buildAdminModelPresetGroups() {
  return [
    {
      key: 'llm',
      label: NGC_MODEL_GROUP.LLM,
      options: NGC_LLM_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.LLM, 'model / deepseekModel')
      ).filter(Boolean)
    },
    {
      key: 'image',
      label: NGC_MODEL_GROUP.IMAGE,
      options: NGC_IMAGE_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.IMAGE, 'seedreamModel')
      ).filter(Boolean)
    },
    {
      key: 'video',
      label: NGC_MODEL_GROUP.VIDEO,
      options: NGC_VIDEO_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.VIDEO, 'seedanceModel')
      ).filter(Boolean)
    },
    {
      key: 'audio',
      label: NGC_MODEL_GROUP.AUDIO,
      options: NGC_AUDIO_MODEL_OPTIONS.map(m =>
        mapCanvasOption(m, NGC_MODEL_GROUP.AUDIO, 'model')
      ).filter(Boolean)
    }
  ]
}

export function buildAdminModelPresetsFlat() {
  const seen = new Set()
  const out = []
  for (const g of buildAdminModelPresetGroups()) {
    for (const opt of g.options || []) {
      if (seen.has(opt.value)) continue
      seen.add(opt.value)
      out.push(opt)
    }
  }
  return out
}

export function findCanvasPresetByModelId(modelId) {
  const id = String(modelId || '').trim()
  if (!id) return null
  return buildAdminModelPresetsFlat().find(o => o.value === id) || null
}
